import { useEffect } from 'react';
const SEO_MARKER = 'data-snapv-seo';
const JSON_LD_ID = 'snapv-jsonld';
function ensureMeta(name, attr = 'name') {
    const selector = `meta[${attr}="${name}"]`;
    let element = document.head.querySelector(selector);
    const created = !element;
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        element.setAttribute(SEO_MARKER, 'true');
        document.head.appendChild(element);
    }
    return { element, created };
}
function ensureLink(rel) {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    const created = !element;
    if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        element.setAttribute(SEO_MARKER, 'true');
        document.head.appendChild(element);
    }
    return { element, created };
}
const TEST_DOMAINS = ['test.snapv.ai', 'www.test.snapv.ai'];
function isTestDomain() {
    return typeof window !== 'undefined' && TEST_DOMAINS.includes(window.location.hostname);
}
export function SeoMeta({ title, description, canonical, noindex = false, jsonLd }) {
    const effectiveNoindex = noindex || isTestDomain();
    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }
        const previousTitle = document.title;
        const titleTag = document.head.querySelector('title');
        const previousTitleText = titleTag?.textContent ?? previousTitle;
        const descriptionMeta = ensureMeta('description');
        const robotsMeta = ensureMeta('robots');
        const ogTitleMeta = ensureMeta('og:title', 'property');
        const ogDescriptionMeta = ensureMeta('og:description', 'property');
        const twitterTitleMeta = ensureMeta('twitter:title', 'property');
        const twitterDescriptionMeta = ensureMeta('twitter:description', 'property');
        const canonicalLink = canonical && !isTestDomain() ? ensureLink('canonical') : null;
        const jsonLdScript = jsonLd
            ? document.head.querySelector(`script#${JSON_LD_ID}`)
            : null;
        const hadExistingJsonLd = Boolean(jsonLdScript);
        document.title = title;
        descriptionMeta.element.content = description;
        robotsMeta.element.content = effectiveNoindex ? 'noindex, nofollow' : 'index, follow';
        ogTitleMeta.element.content = title;
        ogDescriptionMeta.element.content = description;
        twitterTitleMeta.element.content = title;
        twitterDescriptionMeta.element.content = description;
        if (canonical && canonicalLink) {
            canonicalLink.element.href = canonical;
        }
        if (jsonLd) {
            const nextScript = jsonLdScript || document.createElement('script');
            nextScript.id = JSON_LD_ID;
            nextScript.type = 'application/ld+json';
            nextScript.setAttribute(SEO_MARKER, 'true');
            nextScript.textContent = JSON.stringify(jsonLd);
            if (!jsonLdScript) {
                document.head.appendChild(nextScript);
            }
        }
        return () => {
            document.title = previousTitleText;
            if (descriptionMeta.created) {
                descriptionMeta.element.remove();
            }
            if (robotsMeta.created) {
                robotsMeta.element.remove();
            }
            if (ogTitleMeta.created) {
                ogTitleMeta.element.remove();
            }
            if (ogDescriptionMeta.created) {
                ogDescriptionMeta.element.remove();
            }
            if (twitterTitleMeta.created) {
                twitterTitleMeta.element.remove();
            }
            if (twitterDescriptionMeta.created) {
                twitterDescriptionMeta.element.remove();
            }
            if (canonicalLink?.created) {
                canonicalLink.element.remove();
            }
            const currentJsonLd = document.head.querySelector(`script#${JSON_LD_ID}`);
            if (currentJsonLd && !hadExistingJsonLd) {
                currentJsonLd.remove();
            }
        };
    }, [canonical, description, effectiveNoindex, jsonLd, noindex, title]);
    return null;
}
